import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Action, select, Store } from "@ngrx/store";
import { takeUntil } from "rxjs/operators";
import {
  IntroductionEditorComponent,
  MediaEditorComponent,
  NinjaRequirementsBuilderComponent,
} from "src/app/editors/components";
import { CodeAdventureActivity } from "src/app/models";
import { AppStoreState } from "src/app/store";
import { ActivityEditorBaseComponent } from "../activity";
import { ActivityTogglesComponent } from "../activity-toggles";
import { SenseiStepComponent } from "../sensei-step";
import { CodingStepComponent } from "../coding-step";
import { CodeAdventurePlanningStepComponent } from "./code-adventure-planning-step";
import { updateActivity } from "./store/actions";
import { selectCodeAdventureActivity } from "./store/selectors";
import { EditInMakecodeComponent } from "../edit-in-makecode";

@Component({
  selector: "app-code-adventure-activity",
  templateUrl: "./code-adventure-activity.component.html",
  styleUrls: ["./code-adventure-activity.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeAdventureActivityComponent
  extends ActivityEditorBaseComponent
  implements OnInit
{
  constructor(
    protected store$: Store<AppStoreState>,
    private cdr: ChangeDetectorRef
  ) {
    super(store$);
    this.activityType = "Adventure";
  }

  @HostBinding("class") class = "app-code-adventure-activity";

  @ViewChild(IntroductionEditorComponent)
  introduction: IntroductionEditorComponent;

  @ViewChild(ActivityTogglesComponent)
  activityToggles: ActivityTogglesComponent;

  @ViewChild(NinjaRequirementsBuilderComponent)
  ninjaRequirementsBuilder: NinjaRequirementsBuilderComponent;

  @ViewChild(CodeAdventurePlanningStepComponent)
  planningStep: CodeAdventurePlanningStepComponent;

  @ViewChild(CodingStepComponent)
  codingStep: CodingStepComponent;

  @ViewChild(SenseiStepComponent)
  senseiStep: SenseiStepComponent;

  @ViewChild("exampleGame")
  exampleGame: EditInMakecodeComponent;

  @ViewChild("codeEditor")
  starterCode: EditInMakecodeComponent;

  @ViewChild(MediaEditorComponent)
  mediaEditor: MediaEditorComponent;

  get valid(): boolean {
    return !!(
      this.title?.valid &&
      this.introduction?.valid &&
      this.activityToggles?.valid &&
      this.ninjaRequirementsBuilder?.valid &&
      this.planningStep?.valid &&
      this.codingStep?.valid &&
      this.senseiStep?.valid &&
      this.exampleGame?.valid &&
      this.starterCode?.valid &&
      this.mediaEditor?.valid
    );
  }

  get dirty(): boolean {
    return !!(
      this.title?.dirty ||
      this.introduction?.dirty ||
      this.activityToggles?.dirty ||
      this.ninjaRequirementsBuilder?.dirty ||
      this.planningStep?.dirty ||
      this.codingStep?.dirty ||
      this.senseiStep?.dirty ||
      this.exampleGame?.dirty ||
      this.starterCode?.dirty ||
      this.mediaEditor?.dirty
    );
  }

  set activity(activity: CodeAdventureActivity) {
    this.myActivity = activity;
    this.cdr.detectChanges();
  }

  get activity(): CodeAdventureActivity {
    return this.myActivity;
  }

  private myActivity: CodeAdventureActivity;

  ngOnInit(): void {
    this.store$
      .pipe(select(selectCodeAdventureActivity))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((activity) => (this.activity = activity));
  }

  getDeleteAction(): Action {
    throw Error("Not implememted");
  }

  getUpdateAction(): Action {
    return updateActivity({
      activity: {
        ...this.myActivity,
        title: this.title.value,
        introduction: this.introduction.value,
        ninjaRequirements: this.ninjaRequirementsBuilder.requirements,
        hasLowThreshold: this.activityToggles.hasLowThreshold,
        lowThreshold: this.activityToggles.lowThreshold,
        hasPassingThreshold: this.activityToggles.hasPassingThreshold,
        passingThreshold: this.activityToggles.passingThreshold,
        peerReview: this.activityToggles.peerReview,
        numOfReviews: this.activityToggles.numOfReviews,
        planningStep: this.planningStep.value,
        codingStep: this.codingStep.value,
        senseiStep: this.senseiStep.value,
        project: this.exampleGame?.project,
        starterCode: this.starterCode?.project,
      },
      mediaFile: this.mediaEditor.mediaValue,
    });
  }
}
